/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    InventoryBundlesService,
    PicksService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { PickStore } from './pick.store';
import {
    PickCreate,
    PickRead,
    PickSearchParams,
} from '../../entities/pick.dto';
import { Operation } from 'fast-json-patch';
import { InventoryBundleStore } from '../inventory-bundles/inventory-bundle.store';
import { InventoryBundleRead } from '../../entities/inventory-bundle.dto';

@Injectable()
export class PickDataService extends AbstractDataService<PickRead, PickCreate> {
    private readonly inventoryBundleStore = inject(InventoryBundleStore);
    private readonly inventoryBundlesService = inject(InventoryBundlesService);

    protected EntityName = PFWmsEntities.Pick;
    constructor(
        private service: PicksService,
        store: PickStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: PickCreate): Observable<PickRead> {
        return this.service.pickCreate({
            pickViewModelCreateBody: body,
        }) as Observable<PickRead>;
    }
    protected deleteObs$(id: string): Observable<PickRead> {
        return this.service.pickDelete({
            id,
        }) as Observable<PickRead>;
    }
    protected undoDeleteObs$(id: string): Observable<PickRead> {
        return this.service.pickUndoDelete({
            id,
        }) as Observable<PickRead>;
    }
    protected getObs$(id: string): Observable<PickRead> {
        return this.service.pickReadById({
            id,
        }) as Observable<PickRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<PickRead> {
        return this.service.pickUpdatePatch({
            id,
            operation: operations,
        }) as Observable<PickRead>;
    }
    protected searchObs$(
        searchParams: PickSearchParams
    ): Observable<IPagedResult<PickRead>> {
        return this.service.pickSearch(searchParams) as Observable<
            IPagedResult<PickRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<PickRead>>> {
        return this.service.pickGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<PickRead>>
        >;
    }

    pickInventory$(
        inventoryBundleId: string,
        pickToContainerId: string,
        pickedQuantity: number,
        pickLocationId: string
    ) {
        return this.inventoryBundlesService
            .v1InventoryBundlesIdPickPost({
                id: inventoryBundleId,
                inventoryBundlePickViewModel: {
                    containerId: pickToContainerId,
                    pickedQuantity: pickedQuantity,
                    pickLocationId: pickLocationId,
                },
            })
            .pipe(
                tap(result => {
                    result.pick && this.store.update(result.pick as PickRead);
                    result.sourceBundle &&
                        this.inventoryBundleStore.update(
                            result.sourceBundle as InventoryBundleRead
                        );
                    result.destinationBundle &&
                        this.inventoryBundleStore.update(
                            result.destinationBundle as InventoryBundleRead
                        );
                })
            );
    }

    splitPickLocation$(
        warehouseId: string,
        pickId: string,
        pickLocationId: string,
        newQuantity: number
    ) {
        return this.service
            .v1PicksPickIdLocationsPickLocationIdSplitPost({
                pickId,
                pickLocationId,
                pickLocationSplitRequestBody: {
                    warehouseId,
                    quantityNeeded: newQuantity,
                },
            })
            .pipe(tap(pick => pick && this.store.update(pick as PickRead)));
    }

    cancelPick$(pickId: string, pickLocationIds: string[]) {
        return this.service
            .v1PicksPickIdCancelPost({
                pickId,
                cancelPickRequest: {
                    pickLocationIds,
                },
            })
            .pipe(tap(pick => pick && this.store.update(pick as PickRead)));
    }
}
